import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  useLoadScript,
  InfoWindow,
} from "@react-google-maps/api";

import {
  Card,
  CardContent,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
} from "@mui/material";

import SettingsIcon from "@mui/icons-material/Settings";
import RouteIcon from "@mui/icons-material/Route";
import RefreshIcon from "@mui/icons-material/Refresh";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import NearMeIcon from "@mui/icons-material/NearMe";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageHeader from "pages/components/PageHeader";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";

dayjs.extend(duration);

// ======================= CONFIG =======================
const MAPS_LIBRARIES = []; // sem Places — usamos link do Maps + DistanceMatrix
const BASE_MIN_PER_KG = 0.2; // 12s por kg

const mapContainerStyle = { width: "100%", height: "100%" };

// Warehouse (coordenadas do link partilhado)
const WAREHOUSE = {
  nome: "Armazém Central",
  mapsLoc: { lat: 41.419944, lng: -8.32486 },
};

// ======================= UTILS =======================
const minutesToHHmm = (mins) => {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};
const hhmmToMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};
const calcUnloadMinutes = (weightKg, difficultyFactor) => {
  const peso = Math.ceil(Number(weightKg) || 0);
  return peso * BASE_MIN_PER_KG * (difficultyFactor || 1);
};

const truncate = (str, n = 72) => {
  if (!str) return "—";
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
};

// extrai lat/lng de vários formatos de URL do Google Maps
function parseLatLngFromMapsUrl(url) {
  if (!url) return null;
  try {
    const u = decodeURIComponent(url.trim());

    let m = u.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)[,/]/); // /@lat,lng,…
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };

    m = u.match(/[?&]q=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/i); // ?q=lat,lng
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };

    m = u.match(/[?&]ll=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/i); // &ll=lat,lng
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };

    m = u.match(/[?&]center=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/i); // center=lat,lng
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };

    m = u.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/i); // !3dLAT!4dLNG
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };

    return null;
  } catch {
    return null;
  }
}

// janelas
const windowsOf = (s) => {
  const W = [];
  const s1 = hhmmToMinutes(s.windowStart1);
  const e1 = hhmmToMinutes(s.windowEnd1);
  if (s1 != null || e1 != null) W.push([s1 ?? 0, e1 ?? 24 * 60]);
  const s2 = hhmmToMinutes(s.windowStart2);
  const e2 = hhmmToMinutes(s.windowEnd2);
  if (s2 != null || e2 != null) W.push([s2 ?? 0, e2 ?? 24 * 60]);
  return W.sort((a, b) => a[0] - b[0]);
};
const isOpenAt = (arrival, s) => {
  const W = windowsOf(s);
  if (W.length === 0) return true;
  return W.some(([ws, we]) => arrival >= ws && arrival <= we);
};

function applyWindows(arrival, s) {
  const W = windowsOf(s);
  if (W.length === 0) return { arrival, wait: 0, windowFlag: "ok" };
  for (let i = 0; i < W.length; i++) {
    const [ws, we] = W[i];
    if (arrival < ws) return { arrival: ws, wait: ws - arrival, windowFlag: "ok" };
    if (arrival >= ws && arrival <= we) return { arrival, wait: 0, windowFlag: "ok" };
  }
  return { arrival, wait: 0, windowFlag: "violated" };
}

function buildSchedule(stops, departTimeMinutes) {
  let currentStart = departTimeMinutes;
  return stops.map((s) => {
    const travel = s.travelMinutes ?? 0;
    let arrival = currentStart + travel;

    const { arrival: adjArrival, wait, windowFlag } = applyWindows(arrival, s);
    const unload = calcUnloadMinutes(s.weightKg || 0, s.difficulty || 1);
    const end = adjArrival + unload;

    currentStart = end;

    return {
      ...s,
      arrivalMinutes: adjArrival,
      waitMinutes: wait,
      unloadMinutes: unload,
      endMinutes: end,
      windowFlag,
    };
  });
}

// ======================= Distance Matrix + Otimização =======================
// pesos/afinagens (ajusta se quiseres)
const CLUSTER_BONUS = 30; // bónus por cada vizinho “aproveitável”
const URGENCY_BONUS = 60; // bónus por fecho próximo (janela a fechar)

async function getDistanceMatrixMinutes(origins, destinations) {
  const svc = new window.google.maps.DistanceMatrixService();
  return new Promise((resolve) => {
    svc.getDistanceMatrix(
      {
        origins,
        destinations,
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (res, status) => {
        if (status !== "OK" || !res) return resolve(null);
        const mins = res.rows.map((row) =>
          row.elements.map((el) =>
            el?.duration?.value ? Math.round(el.duration.value / 60) : Infinity
          )
        );
        resolve(mins);
      }
    );
  });
}

// haversine para clustering (distância “em linha reta”, rápida)
function haversineKm(a, b) {
  if (!a || !b) return Infinity;
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s1 = Math.sin(dLat / 2) ** 2;
  const s2 = Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s1 + s2));
}

// espera até abrir (∞ se já não dá hoje)
function waitUntilOpen(arrival, stop) {
  const W = windowsOf(stop);
  if (W.length === 0) return 0;
  let best = Infinity;
  for (const [ws, we] of W) {
    if (arrival <= we) {
      const wait = arrival < ws ? ws - arrival : 0;
      if (wait < best) best = wait;
    }
  }
  return Number.isFinite(best) ? best : Infinity;
}

// devolve {wait, slack} mínimo que cumpre alguma janela; null se impossível hoje
function feasibleWaitAndSlack(arrival, stop, serviceMins) {
  const W = windowsOf(stop);
  if (W.length === 0) return { wait: 0, slack: 24 * 60 }; // sempre aberto
  let best = null;
  for (const [ws, we] of W) {
    if (arrival > we) continue; // já passou
    const wait = arrival < ws ? ws - arrival : 0;
    const end = arrival + wait + serviceMins;
    if (end <= we) {
      const slack = we - end; // quanto sobra até fechar
      if (!best || wait < best.wait || (wait === best.wait && slack < best.slack)) {
        best = { wait, slack };
      }
    }
  }
  return best;
}

// devolve o fim mais cedo possível (e a espera) se couber numa das janelas; caso contrário null
function earliestFeasibleEnd(arrival, serviceMins, stop) {
  const W = windowsOf(stop);
  if (W.length === 0) {
    // sempre aberto
    return { end: arrival + serviceMins, wait: 0 };
  }
  let best = null;
  for (const [ws, we] of W) {
    if (arrival > we) continue; // janela já passou
    const start = Math.max(arrival, ws);
    const end = start + serviceMins;
    if (end <= we) {
      if (!best || end < best.end) best = { end, wait: start - arrival };
    }
  }
  return best; // null se não couber hoje
}

// ——— Helpers para simular rota e avaliar custo ———
function simulateRouteOrder(M, stops, orderIdxs, departMinutes) {
  // orderIdxs são índices na matriz (1..n), 0 é o armazém
  let curIdx = 0;
  let t = departMinutes;
  let drive = 0;
  let waits = [];
  let violations = 0;
  const movedFlags = {};

  for (let p = 0; p < orderIdxs.length; p++) {
    const idx = orderIdxs[p];
    const s = stops[idx - 1]; // -1 porque os stops começam em 0 mas na matriz em 1
    const travel = M[curIdx][idx];
    const travelTime = Number.isFinite(travel) ? travel : 0;
    drive += travelTime;

    const arrival = t + travelTime;
    const service = calcUnloadMinutes(s.weightKg || 0, s.difficulty || 1);

    const feas = earliestFeasibleEnd(arrival, service, s); // {end, wait} | null
    let wait = 0;
    if (feas) {
      wait = Math.max(0, feas.wait || 0);
      t = feas.end;
    } else {
      // não cabe em nenhuma janela hoje → conta violação, mas seguimos
      violations += 1;
      t = arrival + service;
    }
    waits.push(wait);
    if (wait > 0) movedFlags[idx] = true;

    curIdx = idx;
  }

  return {
    endTime: t,
    totalDrive: drive,
    totalWait: waits.reduce((a, b) => a + b, 0),
    violations,
    movedFlags,
  };
}

function routeObjective(sim, weights = { wait: 0.5, violation: 10000 }) {
  return sim.totalDrive + weights.wait * sim.totalWait + weights.violation * sim.violations;
}

// ——— Heurística de inserção com janelas ———
// 1) Helper batched: evita o limite de 100 elementos (origins × destinations)
async function getDistanceMatrixMinutesBatched(points) {
  const svc = new window.google.maps.DistanceMatrixService();
  const n = points.length;
  if (n === 0) return [];

  const MAX_CELLS = 100; // limite da API
  const maxOriginsPerReq = Math.max(1, Math.floor(MAX_CELLS / n));
  const matrix = [];

  for (let start = 0; start < n; start += maxOriginsPerReq) {
    const originsSlice = points.slice(start, Math.min(start + maxOriginsPerReq, n));

    // eslint-disable-next-line no-await-in-loop
    const res = await new Promise((resolve) => {
      svc.getDistanceMatrix(
        {
          origins: originsSlice,
          destinations: points,
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
        },
        (r, status) => {
          if (status !== "OK" || !r) return resolve(null);
          resolve(r);
        }
      );
    });

    if (!res) return null;

    res.rows.forEach((row) => {
      matrix.push(
        row.elements.map((el) =>
          el?.duration?.value ? Math.round(el.duration.value / 60) : Infinity
        )
      );
    });
  }

  return matrix; // [n linhas][n cols] em minutos
}

async function optimizeByDistance({
  warehouseLatLng,
  stopsLatLng,
  stops,
  departMinutes,
  params = {},
}) {
  const gdm = new window.google.maps.DistanceMatrixService();
  const coords = [warehouseLatLng, ...stopsLatLng];

  const M = await getDistanceMatrixMinutesBatched(coords);
  if (!M) return stops;

  // knobs vindos da UI (com defaults)
  const CR = Number(params?.clusterRadiusKm ?? 20); // raio cluster (km)
  const CW = Number(params?.clusterWaitMax ?? 30); // espera vizinho (min)
  const CB = Number(params?.clusterBonus ?? 30); // novo
  const UB = Number(params?.urgencyBonus ?? 60); // novo
  const MJ = Number(params?.maxJumpKm ?? 20); // novo

  // estado
  let currentIdx = 0; // 0 = armazém
  let currentTime = departMinutes; // min desde 00:00
  let remaining = stops.map((s, i) => ({ ...s, movedDueToWindow: false, _i: i + 1 }));
  const ordered = [];

  const clusterCountAt = (r, atTime) => {
    const here = coords[r._i];
    let n = 0;
    for (const o of remaining) {
      if (o === r) continue;
      const km = haversineKm(here, coords[o._i]);
      if (km > CR) continue; // antes: CLUSTER_RADIUS_KM
      const svcO = calcUnloadMinutes(o.weightKg || 0, o.difficulty || 1);
      const feasO = earliestFeasibleEnd(atTime, svcO, o);
      if (feasO || waitUntilOpen(atTime, o) <= CW) n++; // antes: CLUSTER_WAIT_MAX
    }
    return n;
  };

  while (remaining.length) {
    // construir lista de candidatos
    const cands = remaining.map((r) => {
      const travel = M[currentIdx][r._i];
      const travelTime = Number.isFinite(travel) ? travel : 0;
      const arrival = currentTime + travelTime;
      const service = calcUnloadMinutes(r.weightKg || 0, r.difficulty || 1);
      const feas = earliestFeasibleEnd(arrival, service, r); // {end, wait} | null

      let endIfTake,
        wait = 0,
        feasible = false;
      if (feas) {
        endIfTake = feas.end;
        wait = Math.max(0, feas.wait || 0);
        feasible = true;
      } else {
        // custo fallback: viagem + mínima espera até abrir (se houver janela restante)
        let best = Infinity;
        for (const [ws, we] of windowsOf(r)) {
          if (arrival > we) continue;
          const w = arrival < ws ? ws - arrival : 0;
          const end = arrival + w + service;
          if (end <= we) best = Math.min(best, travelTime + w);
        }
        endIfTake = arrival + service; // só para ordenação estável
        wait = Number.isFinite(best) ? best - travelTime : Infinity;
      }

      const hereAt = arrival + (feasible ? wait : 0);
      const cluster = clusterCountAt(r, hereAt);

      return { r, travelTime, arrival, service, feasible, endIfTake, wait, cluster };
    });

    // 1) candidatos viáveis agora (respeitam alguma janela já com a nossa chegada)
    // distância (km) do ponto atual para cada candidato
    const kmFromCurrent = (idx) => haversineKm(coords[currentIdx], coords[idx]);

    // candidatos viáveis agora
    let feasibles = cands.filter((c) => c.feasible);

    // 5.a) Regra de “não saltar longe”: se existir pelo menos um viável dentro de MJ km,
    //     limitamos a escolha a esse subconjunto local.
    const localFeas = feasibles.filter((c) => kmFromCurrent(c.r._i) <= MJ);
    if (localFeas.length) feasibles = localFeas;

    let chosen;
    if (feasibles.length) {
      // enriquecer com slack e score com pesos vindos do UI
      const enriched = feasibles.map((c) => {
        const feas = feasibleWaitAndSlack(c.arrival + (c.wait || 0), c.r, c.service);
        const slack = feas ? feas.slack : 24 * 60;

        // score: quanto MENOR melhor
        const urgencyBoost = Math.max(0, UB - Math.min(slack, UB));
        const score = c.endIfTake - CB * c.cluster - urgencyBoost;

        return { ...c, slack, score, km: kmFromCurrent(c.r._i) };
      });

      // no primeiro passo ainda favorecemos densidade; nos restantes, o score manda
      if (ordered.length === 0) {
        enriched.sort((a, b) => b.cluster - a.cluster || a.endIfTake - b.endIfTake || a.km - b.km);
      } else {
        enriched.sort(
          (a, b) => a.score - b.score || b.cluster - a.cluster || a.endIfTake - b.endIfTake
        );
      }
      chosen = enriched[0];
    } else {
      // ninguém cabe agora → minimizar (viagem + espera); se houver alguém local, preferir local
      let later = cands
        .filter((c) => Number.isFinite(c.wait))
        .map((c) => ({ ...c, cost: c.travelTime + c.wait, km: kmFromCurrent(c.r._i) }));

      const localLater = later.filter((c) => c.km <= MJ);
      if (localLater.length) later = localLater;

      if (later.length) {
        later.sort((a, b) => a.cost - b.cost || b.cluster - a.cluster || a.km - b.km);
        chosen = later[0];
      } else {
        // impossível hoje → mais perto (limpeza)
        cands.sort((a, b) => kmFromCurrent(a.r._i) - kmFromCurrent(b.r._i));
        chosen = cands[0];
      }
    }

    // avançar estado
    if (chosen.wait > 0) chosen.r.movedDueToWindow = true;
    currentTime = chosen.feasible ? chosen.endIfTake : chosen.arrival + chosen.service;
    currentIdx = chosen.r._i;

    ordered.push(chosen.r);
    remaining = remaining.filter((x) => x !== chosen.r);
  }

  return ordered;
}

// ======================= DIALOGS =======================
function DialogMorada({ open, onClose, onSave, initialAddress = "", initialLoc = null, isLoaded }) {
  const mapRef = useRef(null);
  const [center, setCenter] = useState(initialLoc || WAREHOUSE.mapsLoc);
  const [marker, setMarker] = useState(initialLoc || null);
  const [address, setAddress] = useState(initialAddress || "");
  const [mapsUrl, setMapsUrl] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoText, setInfoText] = useState("");

  useEffect(() => {
    setCenter(initialLoc || WAREHOUSE.mapsLoc);
    setMarker(initialLoc || null);
    setAddress(initialAddress || "");
    setMapsUrl("");
    setInfoOpen(false);
    setInfoText("");
  }, [initialAddress, initialLoc, open]);

  const reverseGeocode = (latLng) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        setAddress(results[0].formatted_address);
        setInfoText(results[0].formatted_address);
        setInfoOpen(true);
      } else {
        setInfoText(`${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`);
        setInfoOpen(true);
      }
    });
  };

  const applyMapsUrl = () => {
    const loc = parseLatLngFromMapsUrl(mapsUrl);
    if (loc) {
      setMarker(loc);
      setCenter(loc);
      reverseGeocode(loc);
    } else if (mapsUrl.trim() !== "") {
      alert(
        "Não consegui extrair coordenadas desse URL.\nSugestão: abre o local no Google Maps e copia o URL da barra de endereços (não o encurtado maps.app.goo.gl)."
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Editar morada / localização</DialogTitle>
      <DialogContent sx={{ pt: 2, display: "grid", gap: 1.5 }}>
        <TextField
          fullWidth
          label="URL do Google Maps (cola aqui o link da barra de endereços)"
          placeholder="https://www.google.com/maps/place/…/@41.56,-8.31,17z"
          value={mapsUrl}
          onChange={(e) => setMapsUrl(e.target.value)}
          onBlur={applyMapsUrl}
        />
        <TextField
          fullWidth
          label="Descrição / Morada (livre)"
          placeholder="Ex.: Café Avenida, Barcelos"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />

        <div style={{ height: 360, borderRadius: 12, overflow: "hidden" }}>
          {isLoaded ? (
            <GoogleMap
              onLoad={(m) => (mapRef.current = m)}
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={center}
              zoom={marker ? 16 : 13}
              onClick={(e) => {
                const latLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                setMarker(latLng);
                setCenter(latLng);
                reverseGeocode(latLng);
              }}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
            >
              {marker && (
                <>
                  <Marker position={marker} onClick={() => setInfoOpen((v) => !v)} />
                  {infoOpen && (
                    <InfoWindow
                      position={marker}
                      onCloseClick={() => setInfoOpen(false)}
                      options={{ pixelOffset: new window.google.maps.Size(0, -10) }}
                    >
                      <div style={{ maxWidth: 260 }}>
                        <strong>Local selecionado</strong>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          {infoText || `${marker.lat.toFixed(6)}, ${marker.lng.toFixed(6)}`}
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </>
              )}
            </GoogleMap>
          ) : (
            <MDTypography>Carregando mapa…</MDTypography>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <MDButton color="secondary" variant="outlined" onClick={onClose}>
          Cancelar
        </MDButton>
        <MDButton
          color="info"
          variant="contained"
          onClick={() => onSave({ address, mapsLoc: marker || center })}
        >
          Usar esta morada
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function DialogStop({ open, onClose, onSave, stop }) {
  const [form, setForm] = useState(stop || {});
  useEffect(() => setForm(stop || {}), [stop]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Editar cliente / descarga</DialogTitle>
      <DialogContent sx={{ pt: 2, display: "grid", gap: 1.5 }}>
        <TextField
          label="Nome"
          value={form.name || ""}
          onChange={(e) => set("name", e.target.value)}
          fullWidth
        />
        <TextField
          label="Peso (kg)"
          type="number"
          value={form.weightKg ?? 0}
          onChange={(e) => set("weightKg", Number(e.target.value))}
          fullWidth
        />
        <TextField
          label="Dificuldade (ex. 1.2)"
          type="number"
          inputProps={{ step: 0.1 }}
          value={form.difficulty ?? 1}
          onChange={(e) => set("difficulty", Number(e.target.value))}
          fullWidth
        />

        <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
          <TextField
            label="Janela 1 início"
            type="time"
            value={form.windowStart1 || ""}
            onChange={(e) => set("windowStart1", e.target.value)}
            inputProps={{ step: 300 }}
          />
          <TextField
            label="Janela 1 fim"
            type="time"
            value={form.windowEnd1 || ""}
            onChange={(e) => set("windowEnd1", e.target.value)}
            inputProps={{ step: 300 }}
          />
          <TextField
            label="Janela 2 início"
            type="time"
            value={form.windowStart2 || ""}
            onChange={(e) => set("windowStart2", e.target.value)}
            inputProps={{ step: 300 }}
          />
          <TextField
            label="Janela 2 fim"
            type="time"
            value={form.windowEnd2 || ""}
            onChange={(e) => set("windowEnd2", e.target.value)}
            inputProps={{ step: 300 }}
          />
        </MDBox>

        <TextField
          label="URL do Google Maps (opcional, extrai coordenadas)"
          placeholder="https://www.google.com/maps/place/…/@41.56,-8.31,17z"
          value={form.mapsUrl || ""}
          onChange={(e) => set("mapsUrl", e.target.value)}
          onBlur={(e) => {
            const loc = parseLatLngFromMapsUrl(e.target.value);
            if (loc) set("mapsLoc", loc);
          }}
          fullWidth
        />
        <TextField
          label="Morada (texto livre — só informativa)"
          value={form.address || ""}
          onChange={(e) => set("address", e.target.value)}
          fullWidth
        />
        <MDTypography variant="caption" color="text">
          Dica: abre o cliente no Google Maps e copia o URL da barra de endereços; ao sair do campo,
          as coordenadas são extraídas.
        </MDTypography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <MDButton color="secondary" variant="outlined" onClick={onClose}>
          Cancelar
        </MDButton>
        <MDButton color="info" variant="outlined" onClick={() => onSave(form, { openMap: true })}>
          Editar morada/localização…
        </MDButton>
        <MDButton color="info" variant="contained" onClick={() => onSave(form)}>
          Gravar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ======================= PÁGINA =======================
export default function PlaneadorEntregas() {
  const [controller] = useMaterialUIController();
  const GOOGLE_MAPS_KEY = controller?.maps?.googleMapsKey || "";

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: MAPS_LIBRARIES,
  });

  const mapRef = useRef(null);

  const [departHHMM, setDepartHHMM] = useState("08:00");
  const [autoFit, setAutoFit] = useState(true);

  // parâmetros afináveis pelo utilizador
  const [algoParams, setAlgoParams] = useState({
    clusterRadiusKm: 20, // raio p/ “zona”
    clusterWaitMax: 30, // vizinho abre até +N min
    tieSlopMin: 25, // (ainda disponível, mesmo não usado no score)
    clusterBonus: 30, // peso do cluster
    urgencyBonus: 60, // peso da urgência (fecho próximo)
    maxJumpKm: 35, // não saltar > N km se houver alternativa local
  });

  // 10 clientes (demo) já com mapsLoc (≈20km)
  const [stops, setStops] = useState([
    {
      id: "r1",
      name: "Tasquinha do Minho",
      address: "—",
      mapsLoc: { lat: 41.273364886, lng: -8.347206911 },
      weightKg: 100,
      difficulty: 0.9,
      windowStart1: "09:00",
      windowEnd1: "12:30",
      windowStart2: "14:30",
      windowEnd2: "18:30",
    },
    {
      id: "r2",
      name: "Adega do Bom Jesus",
      address: "—",
      mapsLoc: { lat: 41.26077046, lng: -8.23688707 },
      weightKg: 100,
      difficulty: 1.2,
      windowStart1: "10:00",
      windowEnd1: "13:00",
    },
    {
      id: "r3",
      name: "Cantinho do Galo",
      address: "—",
      mapsLoc: { lat: 41.573291627, lng: -8.245826848 },
      weightKg: 100,
      difficulty: 1.1,
      windowStart1: "08:30",
      windowEnd1: "16:00",
    },
    {
      id: "r4",
      name: "Bistro Bastide",
      address: "—",
      mapsLoc: { lat: 41.560897799, lng: -8.316244942 },
      weightKg: 100,
      difficulty: 1.3,
      windowStart1: "09:00",
      windowEnd1: "11:30",
      windowStart2: "15:00",
      windowEnd2: "19:00",
    },
    {
      id: "r5",
      name: "Café Avenida Barcelos",
      address: "—",
      mapsLoc: { lat: 41.515405023, lng: -8.491679625 },
      weightKg: 100,
      difficulty: 1.0,
      windowStart1: "08:00",
      windowEnd1: "12:00",
    },
    {
      id: "r6",
      name: "Churrasqueira Famalicense",
      address: "—",
      mapsLoc: { lat: 41.261762744, lng: -8.293114166 },
      weightKg: 100,
      difficulty: 1.4,
      windowStart1: "12:00",
      windowEnd1: "15:00",
      windowStart2: "18:30",
      windowEnd2: "21:00",
    },
    {
      id: "r7",
      name: "Taberna da Penha",
      address: "—",
      mapsLoc: { lat: 41.581971438, lng: -8.362173915 },
      weightKg: 100,
      difficulty: 0.8,
      windowStart1: "09:30",
      windowEnd1: "17:30",
    },
    {
      id: "r8",
      name: "O Abade de Priscos",
      address: "—",
      mapsLoc: { lat: 41.267181866, lng: -8.338312872 },
      weightKg: 100,
      difficulty: 1.6,
      windowStart1: "11:00",
      windowEnd1: "14:00",
    },
    {
      id: "r9",
      name: "Solar do Vinho Verde",
      address: "—",
      mapsLoc: { lat: 41.357620904, lng: -8.530254842 },
      weightKg: 100,
      difficulty: 0.95,
      windowStart1: "09:00",
      windowEnd1: "12:00",
      windowStart2: "16:00",
      windowEnd2: "19:00",
    },
    {
      id: "r10",
      name: "Marisqueira do Ave",
      address: "—",
      mapsLoc: { lat: 41.519113264, lng: -8.280912905 },
      weightKg: 100,
      difficulty: 1.1,
      windowStart1: "10:30",
      windowEnd1: "13:30",
      windowStart2: "18:00",
      windowEnd2: "22:00",
    },
  ]);

  // capacidade da viatura (editar pelo utilizador)
  const [vehicleCapacityKg, setVehicleCapacityKg] = useState(999);

  // totais de peso
  const totalWeightKg = useMemo(
    () => stops.reduce((sum, s) => sum + (Number(s.weightKg) || 0), 0),
    [stops]
  );
  const overweightKg = Math.max(0, totalWeightKg - (Number(vehicleCapacityKg) || 0));

  const [geos, setGeos] = useState(null);
  const [directions, setDirections] = useState(null);
  const [isComputing, setIsComputing] = useState(false);

  const departMinutes = useMemo(() => hhmmToMinutes(departHHMM) ?? 8 * 60, [departHHMM]);

  // geocode: só quando necessário
  const geocodeAll = useCallback(async () => {
    if (!isLoaded || !window.google) return;
    const geocoder = new window.google.maps.Geocoder();

    const fromMapsLocOrGeocode = (obj) =>
      new Promise((resolve) => {
        if (obj?.mapsLoc?.lat && obj?.mapsLoc?.lng) {
          resolve({ lat: obj.mapsLoc.lat, lng: obj.mapsLoc.lng });
          return;
        }
        if (!obj?.address) {
          resolve(null);
          return;
        }
        geocoder.geocode({ address: obj.address }, (results, status) => {
          if (status === "OK" && results?.[0]?.geometry?.location) {
            const loc = results[0].geometry.location;
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else {
            resolve(null);
          }
        });
      });

    const warehouseLatLng = await fromMapsLocOrGeocode(WAREHOUSE);
    const stopsLatLng = [];
    for (const s of stops) {
      // eslint-disable-next-line no-await-in-loop
      stopsLatLng.push(await fromMapsLocOrGeocode(s));
    }
    setGeos({ warehouseLatLng, stopsLatLng });
  }, [isLoaded, stops]);

  // util: reconstruir geos a partir das mapsLoc (sem geocoder)
  const rebuildGeosFromStops = useCallback(
    (arr) => {
      const warehouseLatLng = geos?.warehouseLatLng || WAREHOUSE.mapsLoc;
      const stopsLatLng = (arr || stops).map((s) => s.mapsLoc || null);
      setGeos({ warehouseLatLng, stopsLatLng });
    },
    [geos?.warehouseLatLng, stops]
  );

  // Directions — mantém a ordem (drag/otimização)
  const computeDirections = useCallback(async () => {
    if (!isLoaded || !window.google || !geos) return;
    const g = window.google;
    const svc = new g.maps.DirectionsService();

    const origin = geos.warehouseLatLng || WAREHOUSE.mapsLoc;
    const destination = geos.stopsLatLng?.[geos.stopsLatLng.length - 1] || origin;
    const waypoints =
      (geos.stopsLatLng || []).length > 1
        ? geos.stopsLatLng.slice(0, geos.stopsLatLng.length - 1).map((p) => ({
            location: p,
            stopover: true,
          }))
        : [];

    setIsComputing(true);
    svc.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: g.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      },
      (res, status) => {
        setIsComputing(false);
        if (status === "OK" && res) {
          setDirections(res);
          const legs = res.routes?.[0]?.legs || [];
          const travelMinutesByStop = stops.map((_, idx) => {
            const secs = legs[idx]?.duration?.value ?? 0;
            return Math.round(secs / 60);
          });
          setStops((curr) => {
            let changed = false;
            const next = curr.map((s, idx) => {
              const tm = travelMinutesByStop[idx] ?? 0;
              if (s.travelMinutes !== tm) {
                changed = true;
                return { ...s, travelMinutes: tm };
              }
              return s;
            });
            return changed ? next : curr;
          });
        } else {
          setDirections(null);
        }
      }
    );
  }, [isLoaded, geos, stops]);

  // Inicial
  useEffect(() => {
    if (isLoaded) geocodeAll();
  }, [isLoaded, stops, geocodeAll]);

  // Depois de geos → rota
  useEffect(() => {
    if (geos) computeDirections();
  }, [geos, computeDirections]);

  // Schedule memo
  const scheduledStops = useMemo(() => buildSchedule(stops, departMinutes), [stops, departMinutes]);

  // Drag & drop
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    const arr = Array.from(stops);
    const [moved] = arr.splice(src, 1);
    arr.splice(dst, 0, moved);
    setStops(arr);
    rebuildGeosFromStops(arr);
  };

  // Fit bounds
  useEffect(() => {
    if (!mapRef.current || !window.google || !autoFit) return;
    const g = window.google;
    const b = new g.maps.LatLngBounds();
    const fallbackStops = stops.map((s) => s.mapsLoc || null);
    const points = geos?.stopsLatLng || fallbackStops;
    const w = geos?.warehouseLatLng || WAREHOUSE.mapsLoc;
    if (w) b.extend(w);
    points.forEach((p) => p && b.extend(p));
    if (!b.isEmpty()) mapRef.current.fitBounds(b);
  }, [geos, autoFit, stops]);

  // Helpers edição
  const updateStopFields = (id, patch) =>
    setStops((curr) => curr.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const sortByWindowEnd = () => {
    const end = (s) => hhmmToMinutes(s.windowEnd1) ?? hhmmToMinutes(s.windowEnd2) ?? 24 * 60;
    const arr = [...stops].sort((a, b) => end(a) - end(b));
    setStops(arr);
    rebuildGeosFromStops(arr);
  };

  // Otimizar por distância (vizinho mais próximo com janelas)
  const optimizeDistance = async () => {
    if (!isLoaded || !window.google) return;
    const warehouseLatLng = geos?.warehouseLatLng || WAREHOUSE.mapsLoc;
    const stopsLatLng = geos?.stopsLatLng?.length
      ? geos.stopsLatLng
      : stops.map((s) => s.mapsLoc || null);

    setIsComputing(true);
    try {
      const ordered = await optimizeByDistance({
        warehouseLatLng,
        stopsLatLng,
        stops,
        departMinutes,
        params: algoParams, // <<< knobs do utilizador
      });

      setStops(ordered);
      rebuildGeosFromStops(ordered);
    } finally {
      setIsComputing(false);
    }
  };

  // Pins numerados (um ícone, sem piscar)
  const makePinSvg = (text, bg = "#2e7d32") => `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36">
      <circle cx="18" cy="18" r="14" fill="${bg}" />
      <text x="18" y="22" text-anchor="middle" font-family="Arial, sans-serif"
        font-size="14" font-weight="700" fill="#ffffff">${text}</text>
    </svg>`;

  const stopIcons = useMemo(() => {
    if (!isLoaded || !window.google) return [];
    const g = window.google;
    return scheduledStops.map((s, i) => {
      const violated = s.windowFlag === "violated";
      const color = violated ? "#ff1744" : s.movedDueToWindow ? "#f9a825" : "#2e7d32";
      const svg = makePinSvg(String(i + 1), color);
      return {
        url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
        scaledSize: new g.maps.Size(30, 30),
        anchor: new g.maps.Point(15, 15),
      };
    });
  }, [isLoaded, scheduledStops]);

  const warehouseIcon = useMemo(() => {
    if (!isLoaded || !window.google) return null;
    const g = window.google;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36">
        <circle cx="18" cy="18" r="14" fill="#1a73e8" />
        <text x="18" y="22" text-anchor="middle" font-family="Arial, sans-serif"
          font-size="14" font-weight="700" fill="#ffffff">W</text>
      </svg>`;
    return {
      url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
      scaledSize: new g.maps.Size(30, 30),
      anchor: new g.maps.Point(15, 15),
    };
  }, [isLoaded]);

  // Popover de detalhes (abre ao clicar na linha)
  const [details, setDetails] = useState({ open: false, anchorEl: null, stopId: null });
  const openDetails = (e, id) => setDetails({ open: true, anchorEl: e.currentTarget, stopId: id });
  const closeDetails = () => setDetails({ open: false, anchorEl: null, stopId: null });

  const selectedStop = scheduledStops.find((s) => s.id === details.stopId);

  // Settings + Morada
  const [dlgStop, setDlgStop] = useState({ open: false, stop: null });
  const [dlgMorada, setDlgMorada] = useState({ open: false, id: null, address: "", loc: null });

  const openSettings = (stop) => setDlgStop({ open: true, stop });
  const closeSettings = () => setDlgStop({ open: false, stop: null });

  const saveStop = (form, opts = {}) => {
    if (!dlgStop.stop) return;
    const patch = { ...form };
    if (form.mapsUrl) {
      const loc = parseLatLngFromMapsUrl(form.mapsUrl);
      if (loc) patch.mapsLoc = loc;
    }
    updateStopFields(dlgStop.stop.id, patch);
    closeSettings();
    if (opts.openMap) {
      setTimeout(() => {
        setDlgMorada({
          open: true,
          id: dlgStop.stop.id,
          address: form.address || dlgStop.stop.address || "",
          loc: patch.mapsLoc || dlgStop.stop.mapsLoc || null,
        });
      }, 50);
    }
    rebuildGeosFromStops(stops.map((s) => (s.id === dlgStop.stop.id ? { ...s, ...patch } : s)));
  };

  const closeMorada = () => setDlgMorada({ open: false, id: null, address: "", loc: null });
  const saveMorada = ({ address, mapsLoc }) => {
    if (dlgMorada.id)
      updateStopFields(dlgMorada.id, { address: address || "", mapsLoc: mapsLoc || null });
    closeMorada();
    setTimeout(() => rebuildGeosFromStops(), 0);
  };

  // Marcadores a mostrar mesmo antes do geocoder
  const displayStopsLatLng = useMemo(() => {
    const fallback = stops.map((s) => s.mapsLoc || null);
    return geos?.stopsLatLng || fallback;
  }, [geos, stops]);

  const [mapInfo, setMapInfo] = useState({ open: false, idx: null });

  if (loadError) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <PageHeader title="Planeador de Entregas" subtitle="Erro ao carregar o Google Maps." />
        {/* === Barra de Parâmetros (acima do mapa/lista) === */}
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader
        title="Planeador de Entregas"
        subtitle="Lista reordenável (drag) + cálculo de horários (duas janelas por cliente) e tempo de descarga por fator de dificuldade."
      />

      {/* Barra de parâmetros abaixo do título */}
      <MDBox px={0}>
        <AlgoParamsBar
          value={algoParams}
          onChange={(patch) => setAlgoParams((p) => ({ ...p, ...patch }))}
        />
      </MDBox>

      {!GOOGLE_MAPS_KEY && (
        <MDBox px={2}>
          <MDTypography color="error" mb={2}>
            ⚠️ Sem Google Maps API Key no contexto. Define em{" "}
            <code>controller.maps.googleMapsKey</code>.
          </MDTypography>
        </MDBox>
      )}

      <MDBox px={0} py={0}>
        <MDBox
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "420px 1fr" }}
          gap={2}
          height={{ xs: "72vh", md: "clamp(64vh, calc(100vh - 240px), 82vh)" }}
        >
          {/* Painel esquerdo */}
          <Card sx={{ overflow: "hidden", height: "100%" }}>
            <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDBox>
                  <MDTypography variant="caption" color="text">
                    Hora de saída
                  </MDTypography>
                  <TextField
                    type="time"
                    size="small"
                    value={departHHMM}
                    onChange={(e) => setDepartHHMM(e.target.value)}
                    inputProps={{ step: 300 }}
                  />
                </MDBox>

                <MDButton
                  variant="contained"
                  color="info"
                  onClick={computeDirections}
                  startIcon={<RouteIcon />}
                  disabled={!isLoaded || isComputing}
                >
                  Recalcular rota
                </MDButton>

                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={sortByWindowEnd}
                  startIcon={<RefreshIcon />}
                >
                  Priorizar fechos cedo
                </MDButton>

                <MDButton
                  variant="outlined"
                  color="warning"
                  onClick={optimizeDistance}
                  startIcon={<NearMeIcon />}
                  disabled={!isLoaded || isComputing}
                >
                  Otimizar por distância
                </MDButton>

                <MDBox display="flex" alignItems="center" ml="auto">
                  <Tooltip title="Ajustar mapa automaticamente aos marcadores">
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MyLocationIcon fontSize="small" />
                      <Switch checked={autoFit} onChange={(e) => setAutoFit(e.target.checked)} />
                    </MDBox>
                  </Tooltip>
                </MDBox>
              </MDBox>

              <Divider sx={{ my: 2 }} />

              {/* Resumo de carga e capacidade */}
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <InfoChip label="Carga total" value={`${totalWeightKg} kg`} />

                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="caption" color="text">
                    Capacidade (kg)
                  </MDTypography>
                  <TextField
                    type="number"
                    size="small"
                    value={vehicleCapacityKg}
                    onChange={(e) => setVehicleCapacityKg(Number(e.target.value || 0))}
                    sx={{ width: 120 }}
                  />
                </MDBox>

                {overweightKg > 0 && (
                  <MDTypography variant="button" color="error" fontWeight="bold" sx={{ ml: 1 }}>
                    Excesso: {overweightKg} kg
                  </MDTypography>
                )}
              </MDBox>

              <MDTypography variant="overline" color="text" sx={{ mb: 1 }}>
                Paragens ({stops.length})
              </MDTypography>

              <MDBox sx={{ flex: 1, minHeight: 0 }}>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="stops-list">
                    {(provided) => (
                      <MDBox
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ height: "100%", overflowY: "auto", pr: 1 }}
                      >
                        {buildSchedule(stops, departMinutes).map((s, index) => {
                          const arrival = minutesToHHmm(s.arrivalMinutes ?? 0);
                          const end = minutesToHHmm(s.endMinutes ?? 0);
                          const violated = s.windowFlag === "violated";

                          return (
                            <Draggable key={s.id} draggableId={s.id} index={index}>
                              {(prov, snapshot) => (
                                <MDBox
                                  ref={prov.innerRef}
                                  {...prov.draggableProps}
                                  {...prov.dragHandleProps}
                                  onClick={(e) => openDetails(e, s.id)}
                                  sx={{
                                    borderRadius: "16px",
                                    p: 1.0,
                                    mb: 1.0,
                                    display: "grid",
                                    gridTemplateColumns: "28px 1fr",
                                    alignItems: "center",
                                    columnGap: 1,
                                    boxShadow: snapshot.isDragging
                                      ? "0 6px 18px rgba(0,0,0,0.2)"
                                      : "0 1px 4px rgba(0,0,0,0.08)",
                                    border: violated
                                      ? "1px solid #ff1744"
                                      : s.movedDueToWindow
                                      ? "1px solid #f9a825"
                                      : "1px solid #eee",
                                    background: violated
                                      ? "rgba(255,23,68,0.05)"
                                      : s.movedDueToWindow
                                      ? "rgba(249,168,37,0.08)"
                                      : "white",
                                    // bgcolor: violated
                                    //   ? "error.main"
                                    //   : s.movedDueToWindow
                                    //   ? "warning.main"
                                    //   : "info.main",
                                    cursor: "pointer",
                                  }}
                                >
                                  {/* ordem */}
                                  <MDBox
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      bgcolor: s.movedDueToWindow
                                        ? "warning.main"
                                        : violated
                                        ? "error.main"
                                        : "info.main",
                                      color: "#fff",
                                      display: "grid",
                                      placeItems: "center",
                                      fontSize: 12,
                                      fontWeight: 700,
                                    }}
                                  >
                                    {index + 1}
                                  </MDBox>

                                  {/* 3 linhas: nome / tempos / morada */}
                                  <MDBox
                                    sx={{
                                      minWidth: 0,
                                      display: "grid",
                                      gridTemplateRows: "auto auto auto",
                                      rowGap: 0.5,
                                    }}
                                  >
                                    {/* 1ª linha: nome + settings */}
                                    <MDBox
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      gap={1}
                                    >
                                      <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{ pr: 1 }}
                                      >
                                        {s.name}
                                      </MDTypography>
                                      <IconButton
                                        size="small"
                                        title="Editar cliente/descarga"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openSettings(s);
                                        }}
                                      >
                                        <SettingsIcon fontSize="small" />
                                      </IconButton>
                                    </MDBox>

                                    {/* 2ª linha: tempos */}
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                      <InfoChip label="Chegada" value={arrival} />
                                      <InfoChip label="Fim" value={end} />
                                    </MDBox>

                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                      sx={{ display: "block" }}
                                      noWrap
                                    >
                                      {s.windowStart1 || s.windowEnd1
                                        ? `${s.windowStart1 || "—"}–${s.windowEnd1 || "—"}`
                                        : "—"}
                                      {s.windowStart2 || s.windowEnd2
                                        ? ` · ${s.windowStart2 || "—"}–${s.windowEnd2 || "—"}`
                                        : ""}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </MDBox>
                    )}
                  </Droppable>
                </DragDropContext>
              </MDBox>
            </CardContent>
          </Card>

          {/* Painel direito: mapa */}
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ p: 0, height: "100%", position: "relative" }}>
              {isLoaded && GOOGLE_MAPS_KEY ? (
                <GoogleMap
                  onLoad={(m) => (mapRef.current = m)}
                  mapContainerStyle={mapContainerStyle}
                  center={WAREHOUSE.mapsLoc}
                  zoom={12}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  {/* Warehouse */}
                  {WAREHOUSE.mapsLoc && (
                    <Marker position={WAREHOUSE.mapsLoc} icon={warehouseIcon} />
                  )}
                  {/* Stops */}
                  {displayStopsLatLng.map(
                    (p, idx) =>
                      p && (
                        <Marker
                          key={stops[idx]?.id || `m${idx}`}
                          position={p}
                          icon={stopIcons[idx]}
                          onClick={() => setMapInfo({ open: true, idx })}
                        />
                      )
                  )}
                  /* InfoWindow com os mesmos dados do popover, abre ao clicar no pin */
                  {mapInfo.open && displayStopsLatLng[mapInfo.idx] && (
                    <InfoWindow
                      position={displayStopsLatLng[mapInfo.idx]}
                      onCloseClick={() => setMapInfo({ open: false, idx: null })}
                    >
                      <div style={{ maxWidth: 260 }}>
                        <strong>{scheduledStops[mapInfo.idx]?.name}</strong>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          {scheduledStops[mapInfo.idx]?.address || "—"}
                        </div>
                        <div
                          style={{
                            marginTop: 6,
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 6,
                          }}
                        >
                          <span>
                            <b>Chegada</b>{" "}
                            {minutesToHHmm(scheduledStops[mapInfo.idx]?.arrivalMinutes || 0)}
                          </span>
                          <span>
                            <b>Fim</b> {minutesToHHmm(scheduledStops[mapInfo.idx]?.endMinutes || 0)}
                          </span>
                          <span>
                            <b>Janela 1</b>
                            <br />
                            {(scheduledStops[mapInfo.idx]?.windowStart1 || "—") +
                              "–" +
                              (scheduledStops[mapInfo.idx]?.windowEnd1 || "—")}
                          </span>
                          <span>
                            <b>Janela 2</b>
                            <br />
                            {(scheduledStops[mapInfo.idx]?.windowStart2 || "—") +
                              "–" +
                              (scheduledStops[mapInfo.idx]?.windowEnd2 || "—")}
                          </span>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                  {/* Rota */}
                  {directions && (
                    <DirectionsRenderer
                      directions={directions}
                      options={{ suppressMarkers: true, polylineOptions: { strokeWeight: 5 } }}
                    />
                  )}
                </GoogleMap>
              ) : (
                <MDBox p={2}>
                  <MDTypography>Carregando mapa… (ou sem API key)</MDTypography>
                </MDBox>
              )}
            </CardContent>
          </Card>
        </MDBox>
      </MDBox>

      {/* POPOVER de detalhes (fundo opaco) */}
      <Popover
        open={details.open}
        anchorEl={details.anchorEl}
        onClose={closeDetails}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 1.5,
            maxWidth: 340,
            bgcolor: "#fff",
            color: "text.primary",
            border: "1px solid #e5e5e5",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        {selectedStop ? (
          <MDBox>
            <MDTypography variant="button" fontWeight="bold">
              {selectedStop.name}
            </MDTypography>
            <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
              {selectedStop.address || "—"}
              {selectedStop.mapsLoc &&
                `  (lat: ${selectedStop.mapsLoc.lat.toFixed(
                  5
                )}, lng: ${selectedStop.mapsLoc.lng.toFixed(5)})`}
            </MDTypography>

            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
              <InfoPill label="Viagem" value={`${selectedStop.travelMinutes ?? 0} min`} />
              <InfoPill
                label="Espera"
                value={selectedStop.waitMinutes ? `${selectedStop.waitMinutes} min` : "—"}
              />
              <InfoPill
                label="Descarga"
                value={`${Math.round(selectedStop.unloadMinutes || 0)} min`}
              />
              <InfoPill label="Peso" value={`${selectedStop.weightKg} kg`} />

              <InfoPill label="Dificuldade" value={String(selectedStop.difficulty)} />
              <InfoPill
                label="Janela 1"
                value={`${selectedStop.windowStart1 || "—"}–${selectedStop.windowEnd1 || "—"}`}
              />
              <InfoPill
                label="Janela 2"
                value={`${selectedStop.windowStart2 || "—"}–${selectedStop.windowEnd2 || "—"}`}
              />
            </MDBox>
          </MDBox>
        ) : (
          <MDTypography variant="caption">—</MDTypography>
        )}
      </Popover>

      {/* Settings + Morada */}
      <DialogStop
        open={dlgStop.open}
        onClose={closeSettings}
        onSave={saveStop}
        stop={dlgStop.stop}
      />
      <DialogMorada
        open={dlgMorada.open}
        onClose={closeMorada}
        onSave={saveMorada}
        initialAddress={dlgMorada.address}
        initialLoc={dlgMorada.loc}
        isLoaded={isLoaded}
      />
    </DashboardLayout>
  );
}

function InfoPill({ label, value }) {
  return (
    <MDBox
      sx={{
        border: "1px solid #eee",
        borderRadius: "999px",
        px: 1.2,
        py: 0.6,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <MDTypography variant="caption" color="text">
        {label}
      </MDTypography>
      <MDTypography variant="caption" fontWeight="bold">
        {value}
      </MDTypography>
    </MDBox>
  );
}

function InfoChip({ label, value }) {
  return (
    <MDBox
      sx={{
        border: "1px solid #e3e3e3",
        borderRadius: "999px",
        px: 1,
        py: 0.3,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        whiteSpace: "nowrap",
      }}
    >
      <MDTypography variant="caption" color="text">
        {label}
      </MDTypography>
      <MDTypography variant="caption" fontWeight="bold">
        {value}
      </MDTypography>
    </MDBox>
  );
}

function AlgoParamsBar({ value, onChange }) {
  const set = (k) => (e) => onChange({ [k]: Math.max(0, Number(e.target.value || 0)) });

  return (
    <MDBox
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.08)",
        background: "rgba(255,255,255,0.9)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(6, minmax(160px, 1fr))",
          },
          gap: 1,
          alignItems: "end",
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="Raio cluster (km)"
          value={value.clusterRadiusKm ?? 20}
          onChange={set("clusterRadiusKm")}
        />
        <TextField
          fullWidth
          size="small"
          label="Espera vizinho (min)"
          value={value.clusterWaitMax ?? 30}
          onChange={set("clusterWaitMax")}
        />

        <TextField
          fullWidth
          size="small"
          label="Bónus cluster"
          value={value.clusterBonus ?? 30}
          onChange={set("clusterBonus")}
        />
        <TextField
          fullWidth
          size="small"
          label="Bónus urgência"
          value={value.urgencyBonus ?? 60}
          onChange={set("urgencyBonus")}
        />
        <TextField
          fullWidth
          size="small"
          label="Máx salto (km)"
          value={value.maxJumpKm ?? 35}
          onChange={set("maxJumpKm")}
        />
      </MDBox>
    </MDBox>
  );
}
